/**
 * Weather and non-weather events on the lane still ahead of the shipment in
 * focus, for the /monitor "On this route" strip. A TV has no hover, so the
 * screen names the few events that matter to this shipment instead of
 * leaving their cards closed.
 */

import {
  getHazardPresentation,
  type HazardAlertLike,
  type HazardIconKey,
} from './hazards/hazardPresentation';
import {
  getHazardRingCenter,
  getHazardTimeWindow,
  getRenderableHazardRings,
} from './hazardTypes';
import { lngDelta, wrapLngNear } from './monitorRouteShape';
import { isPointInPolygon } from './needsAttention';

/** An event this close to the remaining route counts as on it. */
export const LANE_BUFFER_KM = 50;
/** The strip names at most this many events. */
export const LANE_EVENTS_MAX = 2;
/** Route and event edges are sampled at least this often. */
const SAMPLE_STEP_KM = 20;
/** Always look at least this far ahead for events about to start. */
const DEFAULT_LOOKAHEAD_MS = 24 * 60 * 60 * 1000;

type LatLng = { lat: number; lng: number };

export interface LaneEvent {
  /** Matches the event's map icon (see laneEventKey). */
  key: string;
  typeLabel: string;
  title: string;
  severityLabel: string;
  severityColor: string;
  iconKey: HazardIconKey;
  effective: string;
  expiration: string;
  /** Distance along the route to where it first comes within the buffer. */
  aheadKm: number;
  where: 'here' | 'ahead' | 'destination';
  /**
   * ISO start time for an event that has not started yet (it starts before
   * the shipment arrives), else null. The map only draws events already in
   * effect, so an upcoming one has no icon to ring; the strip says when it
   * starts instead.
   */
  startsAt: string | null;
}

const KM_PER_DEG_LAT = 111.32;

/** Fast flat-earth distance; good to a fraction of a km at these ranges. */
const approxKm = (a: LatLng, b: LatLng): number => {
  const meanLat = (((a.lat + b.lat) / 2) * Math.PI) / 180;
  // The short way: the lane may run past ±180 across the date line.
  const dx = lngDelta(a.lng, b.lng) * KM_PER_DEG_LAT * Math.cos(meanLat);
  const dy = (b.lat - a.lat) * KM_PER_DEG_LAT;
  return Math.hypot(dx, dy);
};

/** Points along a polyline no more than SAMPLE_STEP_KM apart. */
const sample = (line: LatLng[]): { point: LatLng; alongKm: number }[] => {
  const out: { point: LatLng; alongKm: number }[] = [];
  let along = 0;
  line.forEach((point, i) => {
    if (i === 0) {
      out.push({ point, alongKm: 0 });
      return;
    }
    const prev = line[i - 1];
    const length = approxKm(prev, point);
    const steps = Math.max(1, Math.ceil(length / SAMPLE_STEP_KM));
    for (let s = 1; s <= steps; s += 1) {
      const t = s / steps;
      out.push({
        point: {
          lat: prev.lat + (point.lat - prev.lat) * t,
          lng: prev.lng + (point.lng - prev.lng) * t,
        },
        alongKm: along + length * t,
      });
    }
    along += length;
  });
  return out;
};

/**
 * A ring with continuous longitudes, starting on the world copy nearest
 * `reference`: 179, -179 becomes 179, 181.
 */
const unwrapRing = (ring: LatLng[], reference: number): LatLng[] => {
  const out: LatLng[] = [];
  ring.forEach((p) => {
    const previous = out.length ? out[out.length - 1].lng : reference;
    out.push({ lat: p.lat, lng: wrapLngNear(p.lng, previous) });
  });
  return out;
};

/**
 * The same key for an event and its map icon: type plus the icon's position
 * (NewsFlash polygons carry no id).
 */
export const laneEventKey = (
  hazardType: string | null | undefined,
  center: LatLng
): string =>
  `${hazardType || ''}|${center.lat.toFixed(4)},${center.lng.toFixed(4)}`;

const severityRank = (level: string | null | undefined): number => {
  const match = /^L(\d)$/.exec(level || '');
  return match ? Number(match[1]) : 0;
};

/**
 * Events whose area crosses or comes within LANE_BUFFER_KM of `route` (the
 * remaining route, current position first), that are still running and
 * start before the shipment arrives. Highest risk first, then nearest.
 */
export const findLaneEvents = ({
  hazards,
  route,
  nowMs,
  arrivalMs,
  startsAtShipment = true,
  bufferKm = LANE_BUFFER_KM,
  max = LANE_EVENTS_MAX,
}: {
  hazards: HazardAlertLike[];
  route: LatLng[];
  nowMs: number;
  arrivalMs: number | null;
  /**
   * False when the lane does not start at the shipment (before pickup it
   * runs from the origin), so nothing on it is "where the shipment is".
   */
  startsAtShipment?: boolean;
  bufferKm?: number;
  max?: number;
}): LaneEvent[] => {
  if (route.length < 2) return [];
  const samples = sample(route);
  const latPad = bufferKm / KM_PER_DEG_LAT;
  const lats = route.map((p) => p.lat);
  // Continuous longitudes (they may run past ±180); hazards are compared on
  // whichever world copy is nearest the lane.
  const lngs = route.map((p) => p.lng);
  const midLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
  const lngPad =
    latPad /
    Math.max(0.1, Math.cos((Math.max(...lats.map(Math.abs)) * Math.PI) / 180));
  const box = {
    minLat: Math.min(...lats) - latPad,
    maxLat: Math.max(...lats) + latPad,
    minLng: Math.min(...lngs) - lngPad,
    maxLng: Math.max(...lngs) + lngPad,
  };
  // Events starting before the shipment arrives, and always the next day: an
  // overdue shipment's ETA is in the past, but it is still on the road.
  const until = Math.max(arrivalMs ?? 0, nowMs + DEFAULT_LOOKAHEAD_MS);

  const found: (LaneEvent & { rank: number })[] = [];
  hazards.forEach((hazard) => {
    const { startMs, endMs } = getHazardTimeWindow(hazard);
    if (endMs != null && endMs <= nowMs) return;
    if (startMs != null && startMs > until) return;
    const rings = getRenderableHazardRings(hazard?.geometry);
    if (rings.length === 0) return;
    const center = getHazardRingCenter(rings[0]);
    if (!center) return;
    // Each area laid out continuously on the lane's world copy, so one that
    // crosses the date line (179°E to 179°W) stays a small area instead of
    // spanning the rest of the world.
    const laidOut = rings.map((ring) => unwrapRing(ring, midLng));
    // Extents overlap: a large area can pass within the buffer of the lane
    // with every corner far outside the lane's box.
    const inBox = laidOut.some((ring) => {
      const lats = ring.map((p) => p.lat);
      const lngs = ring.map((p) => p.lng);
      return (
        Math.max(...lats) >= box.minLat &&
        Math.min(...lats) <= box.maxLat &&
        Math.max(...lngs) >= box.minLng &&
        Math.min(...lngs) <= box.maxLng
      );
    });
    // An area whose extent misses the padded lane box can neither contain a
    // lane point nor come within the buffer: skip it before any sampling or
    // point-in-polygon work (most of a worldwide event set).
    if (!inBox) return;
    const edgeSamples = laidOut.flatMap((ring) =>
      sample(ring).map((s) => s.point)
    );
    // Inside the area, or within the buffer of its edge. A big area can
    // cover the route with every edge far away, hence the inside test.
    const isNear = (point: LatLng) =>
      laidOut.some((ring) =>
        isPointInPolygon(point.lat, wrapLngNear(point.lng, ring[0].lng), ring)
      ) || edgeSamples.some((e) => approxKm(point, e) <= bufferKm);
    const first = samples.find(({ point }) => isNear(point));
    if (!first) return;
    const presentation = getHazardPresentation(hazard);
    let where: LaneEvent['where'] = 'ahead';
    if (startsAtShipment && isNear(route[0])) where = 'here';
    else if (isNear(route[route.length - 1])) where = 'destination';
    const firstAlongKm = first.alongKm;
    found.push({
      key: laneEventKey(hazard.hazard_type, center),
      typeLabel: presentation.typeLabel,
      title: presentation.displayTitle,
      severityLabel: presentation.severityLabel,
      severityColor: presentation.severityColor,
      iconKey: presentation.tooltipIconKey,
      effective: presentation.effective,
      expiration: presentation.expiration,
      aheadKm: firstAlongKm,
      where,
      startsAt:
        startMs != null && startMs > nowMs
          ? new Date(startMs).toISOString()
          : null,
      rank: severityRank(hazard.alert_level),
    });
  });

  return found
    .sort((a, b) => b.rank - a.rank || a.aheadKm - b.aheadKm)
    .slice(0, max)
    .map(({ rank: _rank, ...event }) => event);
};
